<!--
  ConfirmModal.svelte — Reusable yes/no confirmation dialog.

  Wraps flowbite-svelte's Modal with a standard confirmation layout:
  icon, message, cancel + confirm buttons.

  @prop {boolean} open         — Bind to control visibility.
  @prop {string}  title        — Modal title.
  @prop {string}  message      — Confirmation question text.
  @prop {string}  [confirmLabel] — Confirm button text. Default: 'Yes, confirm'.
  @prop {string}  [cancelLabel]  — Cancel button text.  Default: 'Cancel'.
  @prop {'red'|'primary'|'green'} [confirmColor] — Color of the confirm button.
  @prop {boolean} [loading]    — Show spinner on confirm button.
  @event confirm — Fired when the user clicks the confirm button.
  @event cancel  — Fired when the user clicks cancel or closes the modal.
-->
<script lang="ts">
	import { Modal, Button, Spinner } from 'flowbite-svelte';
	import { t } from '../i18n';

	interface Props {
		/** Whether the modal is currently open. */
		open?: boolean;
		/** Modal heading text. */
		title?: string;
		/** The confirmation question displayed to the user. */
		message?: string;
		/** Label for the confirm button. */
		confirmLabel?: string;
		/** Label for the cancel button. */
		cancelLabel?: string;
		/** Color applied to the confirm button. */
		confirmColor?: 'red' | 'primary' | 'green';
		/** Show a spinner and disable the confirm button while processing. */
		loading?: boolean;
		/** Callback fired when the user confirms. */
		onconfirm?: () => void;
		/** Callback fired when the user cancels or closes the modal. */
		oncancel?: () => void;
	}

	let {
		open = $bindable(false),
		title = 'Confirm',
		message = 'Are you sure?',
		confirmLabel = 'Yes, confirm',
		cancelLabel = 'Cancel',
		confirmColor = 'red',
		loading = false,
		onconfirm,
		oncancel
	}: Props = $props();

	/** Close the modal and fire the oncancel callback. */
	function handleCancel(): void {
		open = false;
		oncancel?.();
	}

	/** Fire the onconfirm callback (caller is responsible for closing). */
	function handleConfirm(): void {
		onconfirm?.();
	}
</script>

<Modal bind:open title={$t(title)} size="sm" onclose={handleCancel}>
	<div class="text-center">
		<i class="fa-solid fa-circle-exclamation mx-auto mb-4 text-5xl text-gray-400 dark:text-gray-200"></i>
		<h3 class="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">{$t(message)}</h3>
		<div class="flex justify-center gap-4">
			<Button color="alternative" onclick={handleCancel}>{$t(cancelLabel)}</Button>
			<Button color={confirmColor} disabled={loading} onclick={handleConfirm}>
				{#if loading}<Spinner size="4" class="mr-2" />{/if}
				{$t(confirmLabel)}
			</Button>
		</div>
	</div>
</Modal>
